import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID, registerEnumType, Float } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { Domain, ScalarObject, json5Transformer } from '@things-factory/shell'
import { config } from '@things-factory/env'
import { Kpi, KpiPeriodType } from '../kpi/kpi'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

export enum KpiValueInputType {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO'
}

registerEnumType(KpiValueInputType, {
  name: 'KpiValueInputType',
  description: 'Type of the input (Manual or Auto)'
})

@Entity()
@Index('ix_kpi_value_latest', ['domain', 'kpi', 'valueDate', 'kpiOrgScope', 'version'], { unique: true })
@Index('ix_kpi_value_latest_for_query', ['domain', 'kpi', 'valueDate', 'kpiOrgScope'], { unique: false })
@Index('ix_kpi_value_group_kpi', ['domain', 'group', 'kpi', 'valueDate'], { unique: false })
@Index('ix_kpi_value_kpi_group', ['domain', 'kpi', 'group', 'valueDate'], { unique: false })
@ObjectType({ description: 'Entity for KpiValue' })
export class KpiValue {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for this KPI value record.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain (tenant) to which this KPI value belongs.' })
  domain?: Domain

  @RelationId((kpiValue: KpiValue) => kpiValue.domain)
  @Field({ nullable: true, description: 'ID of the domain (tenant) for this KPI value.' })
  domainId?: string

  @ManyToOne(() => Kpi)
  @Field(type => Kpi, {
    nullable: true,
    description: 'Reference to the KPI definition for which this value is calculated.'
  })
  kpi: Kpi

  @RelationId((kpiValue: KpiValue) => kpiValue.kpi)
  @Field({ description: 'ID of the referenced KPI.' })
  kpiId: string

  @Column('int')
  @Field(type => Int, { description: 'Version of the KPI definition at the time this value was calculated.' })
  version: number

  @Column()
  @Field({
    description:
      'Date or period for which this KPI value is recorded (e.g., day: YYYY-MM-DD, month: YYYY-MM, quarter: YYYY-Qn, range: YYYY-MM-DD~YYYY-MM-DD).'
  })
  valueDate: string

  @Column('float')
  @Field(type => Float, { description: 'The calculated numeric value for this KPI and period.' })
  value: number

  @Column('float', { nullable: true })
  @Field(type => Float, {
    nullable: true,
    description: 'Performance score calculated from KPI value using scoreFormula or grades lookup table.'
  })
  score?: number

  @Column({ nullable: true })
  @Field(type => String, {
    nullable: true,
    description: 'Group key for this value (organization, line, user, project, etc.)'
  })
  group?: string

  @ManyToOne(() => KpiOrgScope, { nullable: true })
  @Field(type => KpiOrgScope, {
    nullable: true,
    description: 'Reference to the organization scope mapping for this KPI value.'
  })
  kpiOrgScope?: KpiOrgScope

  @RelationId((kpiValue: KpiValue) => kpiValue.kpiOrgScope)
  @Field({ nullable: true, description: 'ID of the referenced KPI organization scope.' })
  kpiOrgScopeId?: string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: 'Indicates whether this value was entered manually or generated automatically.'
  })
  inputType?: KpiValueInputType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Source of the value (e.g., system, user, external integration).' })
  source?: string

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined,
    transformer: json5Transformer
  })
  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Extended or non-numeric information related to this KPI value, stored as JSON5. Can include grade, status, comments, or other metadata.'
  })
  meta?: any

  @Column({ default: 'DAY' })
  @Field(type => KpiPeriodType, { description: 'Aggregation period type for this KPI value.' })
  periodType: KpiPeriodType

  @CreateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI value record was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI value record was last updated.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this KPI value record.' })
  creator?: User

  @RelationId((kpiValue: KpiValue) => kpiValue.creator)
  @Field({ nullable: true, description: 'ID of the user who created this KPI value record.' })
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this KPI value record.' })
  updater?: User

  @RelationId((kpiValue: KpiValue) => kpiValue.updater)
  @Field({ nullable: true, description: 'ID of the user who last updated this KPI value record.' })
  updaterId?: string
}
